import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  Checkbox,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT } from "constants/brandColors";
import { useLocales, useTranslationsState, validateNameTranslations } from "utils/translations";
import LocaleAnswerTabs from "./LocaleAnswerTabs";

const BOT_RESPONSES_PAGE_ID = PAGE_IDS.BOT_RESPONSES;

const fieldProps = {
  bg: "white",
  borderColor: GOLD_BORDER,
  focusBorderColor: GOLD_BORDER,
};

const emptyAnswerTranslation = () => ({ answer: "" });

function CreateBotResponse({ onCreated }) {
  const { t } = useTranslation();
  const { locales } = useLocales();
  const [translations, setTranslations, setTranslationField] = useTranslationsState(locales, emptyAnswerTranslation);
  const [type, setType] = useState("FAQ");
  const [trigger, setTrigger] = useState("");
  const [matchType, setMatchType] = useState("CONTAINS");
  const [platform, setPlatform] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [order, setOrder] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const errors = {};
    if (!trigger.trim()) errors.trigger = t("Trigger is required");
    const translationErrors = validateNameTranslations(translations, locales, t, "answer", t("Answer is required"));
    Object.assign(errors, translationErrors);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const translationsPayload = locales
        .map(({ code }) => ({ locale: code, answer: (translations[code]?.answer || "").trim() }))
        .filter((tr) => tr.answer);

      const data = {
        page_id: BOT_RESPONSES_PAGE_ID,
        type,
        trigger: trigger.trim(),
        matchType: type === "FAQ" ? matchType : undefined,
        platform: platform || null,
        isActive,
        ...(order !== "" && { order: Number(order) }),
        translations: translationsPayload,
      };
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/bot/response`,
        "POST",
        data
      );
      if (!response) {
        throw new Error("Failed to create response");
      }
      setTrigger("");
      setOrder("");
      setTranslations(() => {
        const reset = {};
        locales.forEach(({ code }) => { reset[code] = emptyAnswerTranslation(); });
        return reset;
      });
      onCreated?.();
    } catch (err) {
      setError(err.message || t("Failed to create response. Please try again later."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormControl>
      <form onSubmit={handleSubmit}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Type")}</FormLabel>
            <Select value={type} onChange={(e) => setType(e.target.value)} {...fieldProps}>
              <option value="FAQ">{t("FAQ (free-text Q&A)")}</option>
              <option value="COMMAND">{t("Command (e.g. /status)")}</option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Platform")}</FormLabel>
            <Select value={platform} onChange={(e) => setPlatform(e.target.value)} {...fieldProps}>
              <option value="">{t("Both")}</option>
              <option value="TELEGRAM">{t("Telegram")}</option>
              <option value="WHATSAPP">{t("WhatsApp")}</option>
            </Select>
          </FormControl>
        </SimpleGrid>

        <FormControl isInvalid={!!fieldErrors.trigger} mt="16px">
          <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Trigger")}</FormLabel>
          <Input
            isRequired
            placeholder={type === "COMMAND" ? t("/status") : t("The keyword or question users type")}
            value={trigger}
            onChange={(e) => setTrigger(e.target.value)}
            {...fieldProps}
          />
          <FormErrorMessage>{fieldErrors.trigger}</FormErrorMessage>
          {type === "COMMAND" && (
            <Text fontSize="xs" color="gray.500" mt="4px">
              {t("Reserved admin commands (/start, /status, /restart_wa, /qr) already work and can't be overridden here.")}
            </Text>
          )}
        </FormControl>

        {type === "FAQ" && (
          <FormControl mt="16px">
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Match Type")}</FormLabel>
            <Select value={matchType} onChange={(e) => setMatchType(e.target.value)} {...fieldProps}>
              <option value="CONTAINS">{t("Contains (matches anywhere in the message)")}</option>
              <option value="EXACT">{t("Exact (message must match exactly)")}</option>
            </Select>
          </FormControl>
        )}

        <LocaleAnswerTabs
          locales={locales}
          translations={translations}
          errors={fieldErrors}
          onChange={(code, value) => setTranslationField(code, "answer", value)}
          borderColor={GOLD_BORDER}
        />

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt="16px">
          <Checkbox isChecked={isActive} onChange={(e) => setIsActive(e.target.checked)} colorScheme="brand">
            {t("Active")}
          </Checkbox>
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Order")}</FormLabel>
            <Input type="number" placeholder="0" value={order} onChange={(e) => setOrder(e.target.value)} {...fieldProps} />
          </FormControl>
        </SimpleGrid>

        <Button
          type="submit"
          fontSize="sm"
          bg={GOLD_BORDER}
          color="#1A1A1A"
          _hover={{ bg: GOLD_TEXT, color: "white" }}
          _active={{ bg: GOLD_TEXT, color: "white" }}
          fontWeight="500"
          w="100%"
          h="50px"
          mt="20px"
          mb="8px"
          isLoading={isSubmitting}
        >
          {t("Create Response")}
        </Button>
        {error && (
          <Box color="red.500" mt={2} whiteSpace="pre-line">
            {error}
          </Box>
        )}
      </form>
    </FormControl>
  );
}

export default CreateBotResponse;
